import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create separate API instances for different apps
const analyticsApi = axios.create({
  baseURL: `${API_BASE_URL}/analytics`,
  headers: {
    'Content-Type': 'application/json',
  },
});

const tradingApi = axios.create({
  baseURL: `${API_BASE_URL}/trading`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to attach auth token to all requests
const addAuthInterceptor = (apiInstance) => {
  apiInstance.interceptors.request.use((config) => {
    // Don't add auth header to these endpoints (they don't require auth)
    const noAuthEndpoints = ['/login/', '/polling-status/'];
    const needsAuth = !noAuthEndpoints.some(endpoint => config.url.includes(endpoint));

    if (needsAuth) {
      const token = localStorage.getItem('adminToken');
      if (token) {
        config.headers.Authorization = `Basic ${token}`;
      }
    }
    return config;
  }, (error) => {
    return Promise.reject(error);
  });

  // Add response interceptor to handle 401 errors
  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Don't redirect on login endpoint failures
      if (error.response && error.response.status === 401 && !error.config.url.includes('/login/')) {
        // Clear auth data and redirect to login
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUsername');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }
  );
};

// Apply interceptors to all API instances
addAuthInterceptor(api);
addAuthInterceptor(analyticsApi);
addAuthInterceptor(tradingApi);

// Authentication functions
export const login = async (username, password) => {
  try {
    const response = await analyticsApi.post('/login/', { username, password });
    return response.data;
  } catch (error) {
    if (error.response) {
      return error.response.data;
    }
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminUsername');
  localStorage.removeItem('adminRole');
  localStorage.removeItem('allowedMarkets');
  localStorage.removeItem('allowedPages');
  localStorage.removeItem('allowedSymbols');
  localStorage.removeItem('authVersion');
  localStorage.removeItem('sessionStartTime');
  sessionStorage.removeItem('tabActive');
  window.location.href = '/login';
};

// Session timeout in milliseconds (60 minutes)
const SESSION_TIMEOUT_MS = 60 * 60 * 1000;

// Check if session has expired (60 minutes)
export const isSessionExpired = () => {
  const sessionStartTime = localStorage.getItem('sessionStartTime');
  if (!sessionStartTime) return false;

  const elapsed = Date.now() - parseInt(sessionStartTime, 10);
  return elapsed > SESSION_TIMEOUT_MS;
};

// Check if this is a new tab after previous tab was closed
// Uses sessionStorage which is cleared when tab closes
export const isNewTabAfterClose = () => {
  const hasToken = !!localStorage.getItem('adminToken');
  const tabWasActive = !!sessionStorage.getItem('tabActive');

  // If we have a token but tab wasn't marked active, user closed tab and reopened
  return hasToken && !tabWasActive;
};

// Initialize session tracking - call on app load
export const initSessionTracking = () => {
  // Check if session expired
  if (isSessionExpired()) {
    console.log('Session expired (60 minutes), logging out');
    logout();
    return false;
  }

  // Check if this is a new tab after close
  if (isNewTabAfterClose()) {
    console.log('Tab was closed, logging out');
    logout();
    return false;
  }

  // Mark this tab as active
  if (localStorage.getItem('adminToken')) {
    sessionStorage.setItem('tabActive', 'true');
  }

  return true;
};

// Set session start time - call on login
export const setSessionStartTime = () => {
  localStorage.setItem('sessionStartTime', Date.now().toString());
  sessionStorage.setItem('tabActive', 'true');
};

// Check if server auth version matches stored version
// Call this on app load to force logout if version changed
export const checkAuthVersion = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/analytics/auth-version/`);
    if (response.ok) {
      const data = await response.json();
      const serverVersion = data.auth_version?.toString();
      const storedVersion = localStorage.getItem('authVersion');

      // If user is logged in but version mismatch, force logout
      if (localStorage.getItem('adminToken') && storedVersion !== serverVersion) {
        console.log('Auth version mismatch, forcing logout');
        logout();
        return false;
      }
    }
    return true;
  } catch (error) {
    console.error('Error checking auth version:', error);
    return true; // Don't logout on network error
  }
};

// Refresh user permissions from server - call on page load
// This ensures permissions are always up-to-date without requiring logout
export const refreshUserPermissions = async () => {
  const token = localStorage.getItem('adminToken');
  if (!token) return false;

  try {
    const response = await fetch(`${API_BASE_URL}/analytics/me/`, {
      headers: {
        'Authorization': `Basic ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      // Update localStorage with fresh permissions
      localStorage.setItem('adminRole', data.role || 'viewer');
      localStorage.setItem('allowedMarkets', JSON.stringify(data.allowed_markets || ['all']));
      localStorage.setItem('allowedPages', JSON.stringify(data.allowed_pages || ['all']));
      localStorage.setItem('allowedSymbols', JSON.stringify(data.allowed_symbols || {}));
      localStorage.setItem('authVersion', data.auth_version?.toString() || '1');
      return true;
    } else if (response.status === 401) {
      // Token invalid, force logout
      logout();
      return false;
    }
    return false;
  } catch (error) {
    console.error('Error refreshing user permissions:', error);
    return false; // Don't logout on network error
  }
};

export const isAuthenticated = () => {
  return !!localStorage.getItem('adminToken');
};

export const getUserRole = () => {
  return localStorage.getItem('adminRole') || 'viewer';
};

export const isAdmin = () => {
  return getUserRole() === 'admin';
};

export const getAllowedMarkets = () => {
  const markets = localStorage.getItem('allowedMarkets');
  // Return empty array if missing - will trigger re-login via ProtectedRoute
  // Default to empty (no access) instead of ['all'] for security
  return markets ? JSON.parse(markets) : [];
};

export const getAllowedPages = () => {
  const pages = localStorage.getItem('allowedPages');
  // Return empty array if missing - will trigger re-login via ProtectedRoute
  // Default to empty (no access) instead of ['all'] for security
  return pages ? JSON.parse(pages) : [];
};

export const getAllowedSymbols = () => {
  const symbols = localStorage.getItem('allowedSymbols');
  // Returns object like { asia_pacific: ['HSI'] } or {} for no restrictions
  return symbols ? JSON.parse(symbols) : {};
};

// Check if a specific symbol is allowed for the user in a given market
export const isSymbolAllowed = (market, symbolName) => {
  const allowedMarkets = getAllowedMarkets();

  // First check if user has access to the market at all
  if (!allowedMarkets.includes('all') && !allowedMarkets.includes(market)) {
    return false;
  }

  // If user has 'all' markets access, they have all symbols
  if (allowedMarkets.includes('all')) {
    return true;
  }

  // Check symbol-level restrictions
  const allowedSymbols = getAllowedSymbols();

  // If no restrictions for this market, allow all symbols in that market
  if (!allowedSymbols[market]) {
    return true;
  }

  // Check if symbol is in the allowed list for this market
  return allowedSymbols[market].includes(symbolName);
};

// Check if user session has complete privilege data (added after granular access feature)
export const hasCompleteSession = () => {
  return !!(
    localStorage.getItem('adminToken') &&
    localStorage.getItem('allowedPages') &&
    localStorage.getItem('allowedMarkets')
  );
};

export const hasMarketAccess = (market) => {
  const allowed = getAllowedMarkets();
  if (allowed.includes('all')) return true;
  return allowed.includes(market.toLowerCase());
};

export const hasPageAccess = (page) => {
  const allowed = getAllowedPages();
  if (allowed.includes('all')) return true;
  return allowed.includes(page.toLowerCase());
};

export const apiService = {
  // ========================
  // ANALYTICS ENDPOINTS
  // ========================
  
  // Available markets (backend-driven, respects is_active + user permissions)
  getAvailableMarkets: () => analyticsApi.get('/available-markets/'),

  // Dashboard stats
  getDashboardStats: () => analyticsApi.get('/dashboard/stats/'),

  // Polling status (no auth required - handled by interceptor)
  getPollingStatus: () => analyticsApi.get('/polling-status/'),

  // Close price cross-validation
  getClosePriceValidation: (params = {}) => analyticsApi.get('/close-price-validation/', { params }),

  // Users
  getUsers: (params = {}) => analyticsApi.get('/users/', { params }),
  getUser: (id) => analyticsApi.get(`/users/${id}/`),
  
  // Custom Users (for analytics)
  getCustomUsers: (params = {}) => analyticsApi.get('/custom-users/', { params }),
  getCustomUser: (id) => analyticsApi.get(`/custom-users/${id}/`),
  
  // Users list and statistics (analytics endpoints)
  getUsersList: () => analyticsApi.get('/users/list/'),
  getUsersStatistics: () => analyticsApi.get('/users/statistics/'),
  
  // Symbols
  getSymbols: (params = {}) => analyticsApi.get('/symbols/', { params }),
  getSymbol: (id) => analyticsApi.get(`/symbols/${id}/`),
  
  // Markets
  getMarkets: (params = {}) => analyticsApi.get('/markets/', { params }),
  getMarket: (id) => analyticsApi.get(`/markets/${id}/`),
  
  // Investment profiles (these may need to be implemented in trading app)
  getInvestmentProfiles: (params = {}) => tradingApi.get('/user-risk-profiles/', { params }),  // Updated to use trading app
  getInvestmentProfile: (id) => tradingApi.get(`/user-risk-profiles/${id}/`),  // Updated to use trading app
  updateInvestmentProfile: (id, data) => tradingApi.patch(`/user-risk-profiles/${id}/`, data),  // Updated to use trading app
  
  // Index data (moved to analytics)
  getIndexData: (params = {}) => analyticsApi.get('/us-stock-data/', { params }),  // Updated to use analytics app
  getIndexDataByMarket: (marketType) => {
    if (marketType === 'crypto') return analyticsApi.get('/crypto-data/', { params: { latest_only: 'true' } });
    if (marketType === 'india') return analyticsApi.get('/indian-stock-data/', { params: { latest_only: 'true' } });
    if (marketType === 'us') return analyticsApi.get('/us-stock-data/', { params: { latest_only: 'true' } });
    return analyticsApi.get('/us-stock-data/', { params: { market_type: marketType } });
  },
  
  // Market data
  getCryptoData: (params = {}) => analyticsApi.get('/crypto-data/', { params }),
  getIndianStockData: (params = {}) => analyticsApi.get('/indian-stock-data/', { params }),
  getUSStockData: (params = {}) => analyticsApi.get('/us-stock-data/', { params }),
  
  // Polling activity (moved to analytics)
  getPollingActivity: (params = {}) => analyticsApi.get('/polling-logs/', { params }),  // Updated to use analytics app
  getPollingLogs: (params = {}) => analyticsApi.get('/polling-logs/', { params }),
  
  // Data health checks (moved to analytics)
  getDataHealthChecks: (params = {}) => analyticsApi.get('/market-health/', { params }),  // Updated to use analytics app
  getMarketHealthSummary: () => analyticsApi.get('/market-health/'),
  
  // YouTube metrics (moved to analytics)
  getYouTubeMetrics: (params = {}) => analyticsApi.get('/youtube-data/', { params }),  // Updated to use analytics app
  getYouTubeMonthlyStats: () => analyticsApi.get('/youtube-stats/'),
  getYouTubeChannels: (params = {}) => analyticsApi.get('/youtube-channels/', { params }),
  getYouTubeData: (params = {}) => analyticsApi.get('/youtube-data/', { params }),
  
  // ========================
  // TRADING ENDPOINTS
  // ========================
  
  // Exchanges
  getExchanges: (params = {}) => tradingApi.get('/exchanges/', { params }),
  getExchange: (id) => tradingApi.get(`/exchanges/${id}/`),
  createExchange: (data) => tradingApi.post('/exchanges/', data),
  updateExchange: (id, data) => tradingApi.patch(`/exchanges/${id}/`, data),
  deleteExchange: (id) => tradingApi.delete(`/exchanges/${id}/`),
  
  // Instruments
  getInstruments: (params = {}) => tradingApi.get('/instruments/', { params }),
  getInstrument: (id) => tradingApi.get(`/instruments/${id}/`),
  createInstrument: (data) => tradingApi.post('/instruments/', data),
  updateInstrument: (id, data) => tradingApi.patch(`/instruments/${id}/`, data),
  deleteInstrument: (id) => tradingApi.delete(`/instruments/${id}/`),
  
  // User Exchange Accounts
  getUserExchangeAccounts: (params = {}) => tradingApi.get('/user-exchange-accounts/', { params }),
  getUserExchangeAccount: (id) => tradingApi.get(`/user-exchange-accounts/${id}/`),
  createUserExchangeAccount: (data) => tradingApi.post('/user-exchange-accounts/', data),
  updateUserExchangeAccount: (id, data) => tradingApi.patch(`/user-exchange-accounts/${id}/`, data),
  deleteUserExchangeAccount: (id) => tradingApi.delete(`/user-exchange-accounts/${id}/`),

  // Portfolio management
  getPortfolios: (params = {}) => tradingApi.get('/portfolios/', { params }),
  getPortfolio: (id) => tradingApi.get(`/portfolios/${id}/`),
  createPortfolio: (data) => tradingApi.post('/portfolios/', data),
  updatePortfolio: (id, data) => tradingApi.patch(`/portfolios/${id}/`, data),
  deletePortfolio: (id) => tradingApi.delete(`/portfolios/${id}/`),

  getPortfolioCashBalances: (params = {}) => tradingApi.get('/portfolio-cash-balances/', { params }),
  getPortfolioCashBalance: (id) => tradingApi.get(`/portfolio-cash-balances/${id}/`),
  createPortfolioCashBalance: (data) => tradingApi.post('/portfolio-cash-balances/', data),
  updatePortfolioCashBalance: (id, data) => tradingApi.patch(`/portfolio-cash-balances/${id}/`, data),
  deletePortfolioCashBalance: (id) => tradingApi.delete(`/portfolio-cash-balances/${id}/`),

  // User Risk Profiles
  getUserRiskProfiles: (params = {}) => tradingApi.get('/user-risk-profiles/', { params }),
  getUserRiskProfile: (id) => tradingApi.get(`/user-risk-profiles/${id}/`),
  createUserRiskProfile: (data) => tradingApi.post('/user-risk-profiles/', data),
  updateUserRiskProfile: (id, data) => tradingApi.patch(`/user-risk-profiles/${id}/`, data),
  deleteUserRiskProfile: (id) => tradingApi.delete(`/user-risk-profiles/${id}/`),

  // Risk Profile management (per-exchange allocation)
  getRiskProfiles: (params = {}) => tradingApi.get('/risk-profiles/', { params }),
  getRiskProfile: (id) => tradingApi.get(`/risk-profiles/${id}/`),
  createRiskProfile: (data) => tradingApi.post('/risk-profiles/', data),
  updateRiskProfile: (id, data) => tradingApi.patch(`/risk-profiles/${id}/`, data),
  deleteRiskProfile: (id) => tradingApi.delete(`/risk-profiles/${id}/`),

  getRiskProfileAllocations: (params = {}) => tradingApi.get('/risk-profile-allocations/', { params }),
  getRiskProfileAllocation: (id) => tradingApi.get(`/risk-profile-allocations/${id}/`),
  createRiskProfileAllocation: (data) => tradingApi.post('/risk-profile-allocations/', data),
  updateRiskProfileAllocation: (id, data) => tradingApi.patch(`/risk-profile-allocations/${id}/`, data),
  deleteRiskProfileAllocation: (id) => tradingApi.delete(`/risk-profile-allocations/${id}/`),

  // Broker connection check (supports multiple brokers)
  checkBrokerConnection: (params = {}) => tradingApi.get('/check-broker-connection/', { params }),
  
  // Portfolio data (supports multiple brokers)
  getPortfolioData: (params = {}) => tradingApi.get('/get-portfolio-data/', { params }),
  
  // ========================
  // INSTRUMENT VALIDATION (TRADING)
  // ========================
  
  // NOTE: These endpoints need to be implemented in the trading app
  // For now, keeping as core endpoints until they're moved to trading app
  validateTicker: (data) => api.post('/validate-ticker/', data),  // TODO: Move to trading app
  validateAlpacaInstrument: (data) => api.post('/validate-alpaca-instrument/', data),  // TODO: Move to trading app
  checkExistingInstruments: (data) => api.post('/check-existing-instruments/', data),  // TODO: Move to trading app
  syncAlpacaInstruments: (data) => api.post('/sync-alpaca-instruments/', data),  // TODO: Move to trading app
  searchTickers: (params) => api.get('/search-tickers/', { params }),  // TODO: Move to trading app
};

export default api;